/**
 * A pet's four abilities (Pets v3, docs/PETS.md "Pets v3"), parsed from each pets.json pet's `abilities`: an **attack**
 * and a **passive** from level 1, a **pulse** from level 5, a **signature** (once a wave) from level 15, each a `kind`
 * with its numbers. The Muster Pup's are the v2 kinds (bite, muster, rallyBark, howl: their run copies and hashes are
 * unchanged); the other pets use the general kinds (strike, aura, pulse, signature) built from one effect block
 * (`PetHitData`: damage, burn, slow, stun, mark, dazzle). Damage (and a burn's per-second damage) is at level 1 and
 * scales with the towers' level curve in the run copy (meta/petAbilities.ts); everything else is flat.
 */
import type { DamageType } from "./balance";
import { Fix64 } from "../maths/fix64";

/** The four slots, in the pet sheet's order. */
export const PET_ABILITY_SLOTS = ["attack", "passive", "pulse", "signature"] as const;
export type PetAbilitySlot = (typeof PET_ABILITY_SLOTS)[number];

interface AbilityBase { readonly name: string; readonly fromLevel: number }

/** What an ability does to each raider it touches (zeros: none of it). Ticks are sim ticks. */
export interface PetHitData {
  readonly damage: Fix64;
  readonly damageType: DamageType;
  /** Burning: damage per second (the pet's own burn, apart from the towers' stacks), for `burnTicks`. */
  readonly burnPerSecond: Fix64;
  readonly burnTicks: number;
  readonly slowPercent: number;
  readonly slowTicks: number;
  /** Stunned `stunTicks`, a boss `bossStunTicks`. */
  readonly stunTicks: number;
  readonly bossStunTicks: number;
  /** Marked: +`markTakenPercent` damage taken from everything, +`markPointsPercent` kill points, for `markTicks`. */
  readonly markTicks: number;
  readonly markTakenPercent: number;
  readonly markPointsPercent: number;
  /** Dazzled: strikes no troop for `dazzleTicks`. */
  readonly dazzleTicks: number;
}

/** Who an attack picks: ground raiders only, any raider, or a flyer when one is in reach. */
export type StrikeTargets = "ground" | "any" | "airFirst";

export interface BiteData extends AbilityBase { readonly kind: "bite"; readonly damage: Fix64; readonly intervalTicks: number; readonly reach: Fix64 }
/** An attack: the raider nearest the flag within `reach` (by `targets`) takes `damage` (× `airMultPercent` on a flyer) and `hit`. */
export interface StrikeData extends AbilityBase {
  readonly kind: "strike"; readonly damage: Fix64; readonly damageType: DamageType; readonly intervalTicks: number; readonly reach: Fix64;
  readonly targets: StrikeTargets; readonly airMultPercent: number; readonly hit: PetHitData;
}
export interface MusterData extends AbilityBase { readonly kind: "muster"; readonly spawnCutPercent: number }
/** A passive around the flag (`reach`): towers vs flyers, burning raiders take more, troops take less, a heal, kill points. */
export interface AuraData extends AbilityBase {
  readonly kind: "aura"; readonly reach: Fix64; readonly towerAirPercent: number; readonly burningTakenPercent: number; readonly troopGuardPercent: number;
  readonly healEveryTicks: number; readonly healPercent: number; readonly killPointsPercent: number;
}
export interface RallyBarkData extends AbilityBase { readonly kind: "rallyBark"; readonly everyTicks: number; readonly durationTicks: number; readonly reach: Fix64; readonly damagePercent: number; readonly guardPercent: number }
/** Every `everyTicks` of waves: raiders within `reach` (flyers when `air`; only the one with most health when `strongest`) take `hit`; troops there a shield and a heal. */
export interface PulseData extends AbilityBase {
  readonly kind: "pulse"; readonly everyTicks: number; readonly reach: Fix64; readonly air: boolean; readonly strongest: boolean;
  readonly troopShieldPercent: number; readonly shieldTicks: number; readonly troopHealPercent: number; readonly hit: PetHitData;
}
export interface HowlData extends AbilityBase { readonly kind: "howl"; readonly reach: Fix64; readonly stunTicks: number; readonly bossStunTicks: number }
/** Once a wave, the first tick a raider (flyers when `air`) is within `reach`: those there (or the strongest) take `hit`. */
export interface SignatureData extends AbilityBase { readonly kind: "signature"; readonly reach: Fix64; readonly air: boolean; readonly strongest: boolean; readonly hit: PetHitData }

export type AttackData = BiteData | StrikeData;
export type PassiveData = MusterData | AuraData;
export type PulseAbilityData = RallyBarkData | PulseData;
export type SignatureAbilityData = HowlData | SignatureData;
export interface PetAbilitiesData { readonly attack: AttackData; readonly passive: PassiveData; readonly pulse: PulseAbilityData; readonly signature: SignatureAbilityData }
export type PetAbilityKind = PetAbilitiesData[PetAbilitySlot]["kind"];

export const NO_HIT: PetHitData = {
  damage: Fix64.Zero, damageType: 0 as DamageType, burnPerSecond: Fix64.Zero, burnTicks: 0, slowPercent: 0, slowTicks: 0, stunTicks: 0, bossStunTicks: 0,
  markTicks: 0, markTakenPercent: 0, markPointsPercent: 0, dazzleTicks: 0,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= min && v <= max) return v;
  throw new TypeError(`${where}: expected an integer ${min}..${max}, got ${JSON.stringify(v)}`);
}
function fix(v: Json, where: string, allowZero = false): Fix64 {
  const f = Fix64.tryParse(String(v));
  if (f === null || f.isNegative() || (!allowZero && f.isZero())) throw new RangeError(`${where}: expected a number ${allowZero ? ">= 0" : "above 0"}`);
  return f;
}
const opt = (v: Json, d: Json): Json => (v === undefined ? d : v);
/** balance.ts DamageType's values (imported as a type only: balance.ts imports this module). */
const PHYSICAL = 0 as DamageType, MAGIC = 1 as DamageType, BURN = 2 as DamageType;
const DAMAGE_TYPES: Record<string, DamageType> = { physical: PHYSICAL, magic: MAGIC, fire: BURN };
function damageType(v: Json, w: string): DamageType {
  const t = DAMAGE_TYPES[String(opt(v, "physical"))];
  if (t === undefined) throw new Error(`${w}: damageType physical, magic or fire`);
  return t;
}

/** Percent cuts stop short of 100 so a cut interval or a blow is never zero; bonuses stop at 500. */
const MAX_CUT = 90, MAX_BONUS = 500;

function base(a: Json, w: string, maxLevel: number): AbilityBase {
  if (typeof a?.name !== "string" || a.name.length === 0) throw new TypeError(`${w}.name: a name`);
  return { name: a.name, fromLevel: int(a.fromLevel, `${w}.fromLevel`, 1, maxLevel) };
}

/** The effect block (absent fields: none of it). */
export function parseHit(h: Json, w: string): PetHitData {
  if (h === undefined) return NO_HIT;
  const burn = h.burn, slow = h.slow, stun = h.stun, mark = h.mark;
  const stunTicks = stun ? int(stun.ticks, `${w}.stun.ticks`, 1) : 0, bossStunTicks = stun ? int(stun.bossTicks, `${w}.stun.bossTicks`, 0, stunTicks) : 0;
  return {
    damage: fix(opt(h.damage, 0), `${w}.damage`, true), damageType: damageType(h.damageType, w),
    burnPerSecond: burn ? fix(burn.perSecond, `${w}.burn.perSecond`) : Fix64.Zero, burnTicks: burn ? int(burn.ticks, `${w}.burn.ticks`, 1) : 0,
    slowPercent: slow ? int(slow.percent, `${w}.slow.percent`, 1, MAX_CUT) : 0, slowTicks: slow ? int(slow.ticks, `${w}.slow.ticks`, 1) : 0,
    stunTicks, bossStunTicks,
    markTicks: mark ? int(mark.ticks, `${w}.mark.ticks`, 1) : 0, markTakenPercent: mark ? int(opt(mark.takenPercent, 0), `${w}.mark.takenPercent`, 0, MAX_BONUS) : 0,
    markPointsPercent: mark ? int(opt(mark.pointsPercent, 0), `${w}.mark.pointsPercent`, 0, MAX_BONUS) : 0,
    dazzleTicks: int(opt(h.dazzleTicks, 0), `${w}.dazzleTicks`),
  };
}

function attack(a: Json, w: string, maxLevel: number): AttackData {
  const b = base(a, w, maxLevel);
  if (a.kind === "bite") return { ...b, kind: "bite", damage: fix(a.damage, `${w}.damage`), intervalTicks: int(a.intervalTicks, `${w}.intervalTicks`, 1), reach: fix(a.reach, `${w}.reach`) };
  if (a.kind !== "strike") throw new Error(`${w}.kind: bite or strike`);
  const targets = String(opt(a.targets, "ground"));
  if (targets !== "ground" && targets !== "any" && targets !== "airFirst") throw new Error(`${w}.targets: ground, any or airFirst`);
  return {
    ...b, kind: "strike", damage: fix(a.damage, `${w}.damage`), damageType: damageType(a.damageType, w), intervalTicks: int(a.intervalTicks, `${w}.intervalTicks`, 1),
    reach: fix(a.reach, `${w}.reach`), targets, airMultPercent: int(opt(a.airMultPercent, 100), `${w}.airMultPercent`, 1, MAX_BONUS), hit: parseHit(a.hit, `${w}.hit`),
  };
}

function passive(a: Json, w: string, maxLevel: number): PassiveData {
  const b = base(a, w, maxLevel);
  if (a.kind === "muster") return { ...b, kind: "muster", spawnCutPercent: int(a.spawnCutPercent, `${w}.spawnCutPercent`, 0, MAX_CUT) };
  if (a.kind !== "aura") throw new Error(`${w}.kind: muster or aura`);
  const healEveryTicks = int(opt(a.healEveryTicks, 0), `${w}.healEveryTicks`), healPercent = int(opt(a.healPercent, 0), `${w}.healPercent`, 0, 100);
  if ((healEveryTicks > 0) !== (healPercent > 0)) throw new Error(`${w}: healEveryTicks and healPercent together`);
  return {
    ...b, kind: "aura", reach: fix(a.reach, `${w}.reach`), towerAirPercent: int(opt(a.towerAirPercent, 0), `${w}.towerAirPercent`, 0, MAX_BONUS),
    burningTakenPercent: int(opt(a.burningTakenPercent, 0), `${w}.burningTakenPercent`, 0, MAX_BONUS), troopGuardPercent: int(opt(a.troopGuardPercent, 0), `${w}.troopGuardPercent`, 0, MAX_CUT),
    healEveryTicks, healPercent, killPointsPercent: int(opt(a.killPointsPercent, 0), `${w}.killPointsPercent`, 0, MAX_BONUS),
  };
}

function pulse(a: Json, w: string, maxLevel: number): PulseAbilityData {
  const b = base(a, w, maxLevel);
  if (a.kind === "rallyBark") {
    const r: RallyBarkData = {
      ...b, kind: "rallyBark", everyTicks: int(a.everyTicks, `${w}.everyTicks`, 1), durationTicks: int(a.durationTicks, `${w}.durationTicks`, 1), reach: fix(a.reach, `${w}.reach`),
      damagePercent: int(a.damagePercent, `${w}.damagePercent`, 0, MAX_BONUS), guardPercent: int(a.guardPercent, `${w}.guardPercent`, 0, MAX_CUT),
    };
    if (r.durationTicks > r.everyTicks) throw new RangeError(`${w}: durationTicks at most everyTicks`);
    return r;
  }
  if (a.kind !== "pulse") throw new Error(`${w}.kind: rallyBark or pulse`);
  const troopShieldPercent = int(opt(a.troopShieldPercent, 0), `${w}.troopShieldPercent`, 0, MAX_BONUS), shieldTicks = int(opt(a.shieldTicks, 0), `${w}.shieldTicks`);
  if ((troopShieldPercent > 0) !== (shieldTicks > 0)) throw new Error(`${w}: troopShieldPercent and shieldTicks together`);
  return {
    ...b, kind: "pulse", everyTicks: int(a.everyTicks, `${w}.everyTicks`, 1), reach: fix(a.reach, `${w}.reach`), air: a.air === true, strongest: a.strongest === true,
    troopShieldPercent, shieldTicks, troopHealPercent: int(opt(a.troopHealPercent, 0), `${w}.troopHealPercent`, 0, 100), hit: parseHit(a.hit, `${w}.hit`),
  };
}

function signature(a: Json, w: string, maxLevel: number): SignatureAbilityData {
  const b = base(a, w, maxLevel);
  if (a.kind === "howl") {
    const stunTicks = int(a.stunTicks, `${w}.stunTicks`, 1);
    return { ...b, kind: "howl", reach: fix(a.reach, `${w}.reach`), stunTicks, bossStunTicks: int(a.bossStunTicks, `${w}.bossStunTicks`, 0, stunTicks) };
  }
  if (a.kind !== "signature") throw new Error(`${w}.kind: howl or signature`);
  return { ...b, kind: "signature", reach: fix(a.reach, `${w}.reach`), air: a.air === true, strongest: a.strongest === true, hit: parseHit(a.hit, `${w}.hit`) };
}

/** A pet's `abilities`: { attack, passive, pulse, signature }, each with its `kind`. Throws on anything else. */
export function parsePetAbilities(a: Json, w: string, maxLevel: number): PetAbilitiesData {
  if (typeof a !== "object" || a === null) throw new TypeError(`${w}: the four abilities`);
  return { attack: attack(a.attack, `${w}.attack`, maxLevel), passive: passive(a.passive, `${w}.passive`, maxLevel), pulse: pulse(a.pulse, `${w}.pulse`, maxLevel), signature: signature(a.signature, `${w}.signature`, maxLevel) };
}
